import { useEffect, useRef, useState } from 'react';
import { Transition } from '@headlessui/react';
import DataTable from '../table/DataTable';
import { DensityType, TableColumn } from '../table/DataTable.types';
import { useClickOut } from '../../hooks/useClickOut';
import { XCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { RowHeading } from './SelectAutocompleteTable';

interface Props {
  data: any[];
  columns: (op: any) => TableColumn[];
  onChange: (value: any) => void;
  onSelected?: (value: any) => void;
  onClick?: () => void;
  labelKey: string;
  valueKey: string;
  value?: any;
  unit?: string;
  placeholder?: string;
  name?: string;
  title?: string;
  inputClassName?: string;
  modalClassName?: string;
  aboveRowHeading?: RowHeading[];
  titleClassName?: string;
  displayValue?: string;
  open: boolean;
  showResult?: boolean;
  showClearButton?: boolean;
  fulltextSearch?: boolean;
  onClose: () => void;
  useSelection?: boolean;
}

/**
 * Modal selection by table with filters
 */
export default function SelectFilteredModalTable({
  data,
  columns,
  onChange,
  onSelected,
  onClick,
  value,
  unit = '%',
  labelKey,
  valueKey,
  placeholder,
  name,
  title,
  inputClassName,
  modalClassName,
  aboveRowHeading,
  titleClassName,
  displayValue,
  showResult = true,
  showClearButton,
  open,
  fulltextSearch,
  onClose,
  useSelection,
}: Props) {
  const modalRef = useRef<any>();
  const clickOut = useClickOut(modalRef);
  const [localData, setLocalData] = useState<any[]>(data);
  const [selected, setSelected] = useState<any>();
  const [inputValue, setInputValue] = useState('');
  /**
   * Selects item, sets input value
   */
  const handleSelect = (selected?: any) => {
    if (!selected) return;
    const opt = data.find((x: any) => x[valueKey] === selected);
    if (opt) setInputValue(displayValue ? opt[displayValue] : opt[labelKey]);
    onClose();
    setSelected(opt);
    onSelected && onSelected(opt);
  };

  useEffect(() => {
    if (inputValue === '' && selected && selected[labelKey] !== undefined) setSelected({});
    if (selected && selected[labelKey] === inputValue) {
      onChange(selected);
    }
  }, [selected, inputValue]);

  useEffect(() => {
    if (value === null || value === undefined) return;
    const tmpInputVal = localData.find((x: any) => x[valueKey] === value);
    // item found in localData
    if (value !== null && value !== undefined && tmpInputVal) {
      setInputValue(value);
      handleSelect(localData[localData.findIndex((x: any) => x[valueKey] === value)]);
    }
    // item not found - show textBox
    if (value !== null && value !== undefined && !tmpInputVal) {
      setInputValue(value);
    }
  }, [value]);

  // select & close modal
  useEffect(() => {
    if (value === null || value === undefined || !data) return;
    const _selected = data.find((x: any) => x[valueKey] === value);
    if (_selected) setSelected(_selected);
    onClose();
  }, [value]);

  // set local data - for filtering
  useEffect(() => {
    setLocalData(data);
  }, [data]);

  // Close modal if clicked outside
  useEffect(() => {
    if (clickOut) onClose();
  }, [clickOut]);

  // Resets input value
  const reset = () => {
    onChange(null);
    setInputValue('');
  };

  // disable scroll while modal is open
  /*   useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    if (!open) document.body.style.overflow = 'auto';
  }, [open]); */

  return (
    <div className="w-full">
      {inputValue !== null && inputValue !== undefined && inputValue !== '' && showClearButton && (
        <div className="relative">
          <XCircleIcon
            className={`absolute cursor-pointer h-5 top-3 right-2             
          w-5 h-5 text-gray-400 z-10`}
            onClick={() => reset()}
          />
        </div>
      )}
      {showResult && (
        <div className="relative" onClick={onClick}>
          <input
            name={name}
            className={`pl-1 w-full rounded-md block cursor-pointer placeholder-gray-500 text-gray-900 ${inputClassName}`}
            placeholder={placeholder}
            value={
              inputValue !== undefined && inputValue !== null && inputValue !== ''
                ? inputValue + unit
                : ''
            }
            onChange={(e) => {
              onChange(e.target.value);
              setInputValue(e.target.value);
            }}
            readOnly={true}
          />
          {title && (
            <label
              htmlFor={name}
              hidden={inputValue === ''}
              className={`absolute text-sm text-gray-500 text-gray-400 duration-200 transform -translate-y-5 scale-85 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-gray-300 peer-focus:dark:text-gray-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-85 peer-focus:-translate-y-5 left-1 ${titleClassName}`}
            >
              {title}
            </label>
          )}
        </div>
      )}
      <Transition
        className={'random-class'}
        ref={modalRef}
        show={open}
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div
          id="filter-modal-table"
          className={`absolute border mt-1 max-h-[40%] min-h-[250px] overflow-auto overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-50 ml-auto mr-auto left-0 ${modalClassName}`}
        >
          <XMarkIcon
            className="h-6 w-6 absolute right-0 cursor-pointer hover:text-blue-500"
            onClick={onClose}
          />
          <div className="mt-3">
            <DataTable
              data={data}
              columns={columns(handleSelect)}
              density={DensityType.COMPACT}
              columnFilters
              fulltextSearch={fulltextSearch}
              isModalTable
              useSelection={useSelection}
              onSelected={onSelected}
              aboveRowHeading={aboveRowHeading}
            />
          </div>
        </div>
      </Transition>
    </div>
  );
}
